import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";

import { Property } from "../../../../global/types";
import api from "../../../../services/api";
import Skeleton from "./Skeleton";
import PropertyCard from "../../../../components/PropertyCard";
import {
  DescriptionTitle,
  Dot,
  NewcomersCardWrapper,
  NewcomersPropertiesDots,
  NewcomersPropertiesWrapper,
  ScrollButton,
  SeeMoreLink,
  TitleSeeMoreWrapper,
  Wrapper,
} from "./styles";
import arrowRight from "../../../../assets/pages/NewHome/NewcomersSection/arrow-right.svg";
import arrowLeft from "../../../../assets/pages/NewHome/NewcomersSection/arrow-left.svg";

const NewcomersPropertyCard = ({ property, isInitiallyFavorite }: any) => {
  return (
    <NewcomersCardWrapper>
      <PropertyCard
        property={property}
        isInitiallyFavorite={isInitiallyFavorite}
      />
    </NewcomersCardWrapper>
  );
};

function NewcomersSection() {
  const [properties, setProperties] = React.useState<Property[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [activeDot, setActiveDot] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);

  const carousselRef = useRef<HTMLDivElement | null>(null);

  const setCarousselRef = useCallback((node: HTMLDivElement | null) => {
    carousselRef.current = node;
    if (node) {
      checkScroll();
    }
  }, []);

  function checkScroll() {
    const element = carousselRef.current;
    if (element) {
      setIsScrollable(element.scrollWidth > element.clientWidth);
    }
  }

  useEffect(() => {
    getNewcomersPropertiesHandle();
    checkScroll();
    window.addEventListener("resize", checkScroll);

    return () => {
      window.removeEventListener("resize", checkScroll);
    };
  }, []);

  const getNewcomersPropertiesHandle = async () => {
    try {
      const response = await api.get("/api/client/properties", {
        params: { focus: true },
      });
      setProperties(response.data.data.slice(0, 4));
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setLoading(false);
    }
  };

  function handleDotClick(dot: number) {
    if (activeDot === dot) return;

    const element = carousselRef.current;
    if (!element) return;

    const { scrollWidth, clientWidth } = element;
    const cardWidth = element.children[0]?.clientWidth || 300;
    const gap = 16;
    const totalCardWidth = cardWidth + gap;

    const scrollPosition = dot * totalCardWidth;

    element.scrollTo({
      left: scrollPosition,
      behavior: "smooth",
    });

    setActiveDot(dot);
  }

  function handleScroll() {
    const element = carousselRef.current;
    if (!element) return;

    const { scrollLeft } = element;
    const cardWidth = element.children[0]?.clientWidth || 300;
    const gap = 16;
    const totalCardWidth = cardWidth + gap;

    const currentDot = Math.round(scrollLeft / totalCardWidth);
    setActiveDot(currentDot);
  }

  function handleScrollRight() {
    const element = carousselRef.current;
    if (!element) return;

    const { scrollLeft, clientWidth } = element;
    const cardWidth = element.children[0]?.clientWidth || 300;
    const gap = 16;
    const totalCardWidth = cardWidth + gap;

    element.scrollTo({
      left: scrollLeft + clientWidth - gap,
      behavior: "smooth",
    });
  }

  function handleScrollLeft() {
    const element = carousselRef.current;
    if (!element) return;

    const { scrollLeft, clientWidth } = element;
    const cardWidth = element.children[0]?.clientWidth || 300;
    const gap = 16;
    const totalCardWidth = cardWidth + gap;

    element.scrollTo({
      left: scrollLeft - clientWidth + gap,
      behavior: "smooth",
    });
  }

  const calculateDots = () => {
    if (!carousselRef.current || properties.length <= 1) return 0;

    const element = carousselRef.current;
    const cardWidth = element.children[0]?.clientWidth || 300;
    const gap = 16;
    const cardsPerView = Math.floor(element.clientWidth / (cardWidth + gap));

    return Math.max(1, properties.length - cardsPerView + 1);
  };

  const dotsCount = calculateDots();

  return (
    <Wrapper>
      <TitleSeeMoreWrapper>
        <DescriptionTitle>Imóveis Recentes</DescriptionTitle>
        <SeeMoreLink to="/search/?city=Fortaleza&UF=CE">
          <span>Ver mais imóveis</span>
          <ChevronRight aria-hidden />
        </SeeMoreLink>
      </TitleSeeMoreWrapper>

      {!loading && (
        <>
          <NewcomersPropertiesWrapper
            ref={setCarousselRef}
            onScroll={handleScroll}
          >
            {isScrollable && (
              <ScrollButton onClick={handleScrollLeft} className="left">
                <img src={arrowLeft} alt="Anterior" />
              </ScrollButton>
            )}

            {properties.map((property) => (
              <NewcomersPropertyCard
                key={property.id}
                property={property}
                isInitiallyFavorite={false}
              />
            ))}

            {isScrollable && (
              <ScrollButton onClick={handleScrollRight} className="right">
                <img src={arrowRight} alt="Próximo" />
              </ScrollButton>
            )}
          </NewcomersPropertiesWrapper>

          {isScrollable && dotsCount > 1 && (
            <NewcomersPropertiesDots>
              {Array.from({ length: dotsCount }).map((_, index) => (
                <Dot
                  key={index}
                  className={activeDot === index ? "active" : undefined}
                  onClick={() => handleDotClick(index)}
                />
              ))}
            </NewcomersPropertiesDots>
          )}
        </>
      )}

      {loading && <Skeleton />}
    </Wrapper>
  );
}

export default NewcomersSection;